<template>
  <div class="flex flex-col mt-6 justify-between gap-4">
    <v-autocomplete
      @update:model-value="setCompany"
      :model-value="company ?? undefined"
      class="w-full"
      color="primary20"
      label="Empresa"
      theme="light"
      :items="listCompanies"
      item-title="title"
      item-value="value"
      :value-comparator="isSameId"
      variant="outlined"
      clearable
      density="compact"
      hide-details="auto"
    />
    <v-autocomplete
      @update:model-value="setHealthUnits"
      :model-value="healthUnits ?? undefined"
      class="w-full"
      color="primary20"
      label="Unidades"
      :items="listHealthUnitsByCompany"
      item-title="title"
      item-value="value"
      :value-comparator="isSameId"
      variant="outlined"
      density="compact"
      chips
      multiple
      clearable
      :disabled="!company"
      hide-details="auto"
    />
    <v-autocomplete
      @update:model-value="setSchedules"
      :model-value="schedules ?? undefined"
      class="w-full"
      color="primary20"
      label="Escalas"
      theme="light"
      :items="listSchedules"
      item-title="title"
      item-value="value"
      :value-comparator="isSameId"
      variant="outlined"
      density="compact"
      chips
      multiple
      clearable
      :disabled="!healthUnits?.length"
      hide-details="auto"
    >
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item?.raw?.title" :subtitle="item?.raw?.subtitle" />
      </template>
    </v-autocomplete>

    <v-autocomplete
      @update:model-value="setShifts"
      :model-value="shifts ? shifts : undefined"
      class="w-full"
      color="primary20"
      label="Turnos"
      theme="light"
      :items="listShifts"
      item-title="name"
      item-value="id"
      variant="outlined"
      density="compact"
      chips
      multiple
      clearable
      :disabled="!schedules?.length"
      hide-details="auto"
    >
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item?.raw?.name" />
      </template>
      <template v-slot:chip="{ props, item }">
        <v-chip color="gray" v-bind="props" :text="item.raw.name" />
      </template>
    </v-autocomplete>

    <v-autocomplete
      @update:model-value="setPositions"
      :model-value="positions ? positions : undefined"
      class="w-full"
      color="primary20"
      label="Posições"
      theme="light"
      :items="listPositions"
      item-title="name"
      item-value="id"
      variant="outlined"
      density="compact"
      chips
      multiple
      clearable
      :disabled="!schedules?.length"
      hide-details="auto"
    >
      <template v-slot:item="{ props, item }">
        <v-list-item
          v-bind="props"
          :title="item?.raw?.name"
          :subtitle="item?.raw?.schedule?.name"
        />
      </template>
      <template v-slot:chip="{ props, item }">
        <v-chip color="gray" v-bind="props" :text="item.raw.name" />
      </template>
    </v-autocomplete>

    <div v-if="!hideOpenDoctorStation" class="flex flex-col gap-2">
      <span class="text-xs font-medium text-gray-500 ml-1">Situação da Vaga</span>
      <div
        :title="openDoctorStationDisabledHint || undefined"
        data-testid="open-doctor-station-toggle"
        :data-disabled="!!openDoctorStationDisabledHint"
      >
        <v-btn-toggle
          :disabled="!!openDoctorStationDisabledHint"
          :model-value="isOpenDoctorStation === undefined ? 'all' : isOpenDoctorStation"
          @update:model-value="setIsOpenDoctorStation"
          color="primary20"
          variant="outlined"
          divided
          density="compact"
          class="w-full max-w-full"
        >
          <v-btn value="all" class="flex-1 !text-xs md:!text-sm !px-2 md:!px-4">Todas</v-btn>
          <v-btn :value="true" class="flex-1 !text-xs md:!text-sm !px-2 md:!px-4">Abertas</v-btn>
          <v-btn :value="false" class="flex-1 !text-xs md:!text-sm !px-2 md:!px-4"
            >Preenchidas</v-btn
          >
        </v-btn-toggle>
      </div>
    </div>

    <v-combobox
      @update:model-value="setDoctors"
      :model-value="doctors ? doctors : undefined"
      :items="listDoctorsCompany"
      multiple
      chips
      clearable
      label="Médicos"
      class="w-full"
      color="primary20"
      variant="outlined"
      density="compact"
      :disabled="!company || isOpenDoctorStation === true"
      item-title="title"
      item-value="value"
      :return-object="false"
      hide-details="auto"
      :hint="isOpenDoctorStation === true ? 'Desabilitado: Buscando apenas vagas abertas' : ''"
      persistent-hint
    >
      <template v-slot:item="{ props, item }">
        <v-list-item v-bind="props" :title="item.raw.title" :subtitle="item.raw.subtitle" />
      </template>
    </v-combobox>
  </div>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useDoctorStore } from '@/pinia-store/useDoctorStore'
import { usePositionStore } from '@/pinia-store/usePositionStore'
import { useShiftStore } from '@/pinia-store/useShiftStore'
import { isSameId, toId, type SelectId } from '@/utils/selectId'

defineProps([
  'company',
  'healthUnits',
  'schedules',
  'doctors',
  'positions',
  'shifts',
  'isOpenDoctorStation',
  // Texto do tooltip; quando vem, o toggle "Situação da Vaga" fica desabilitado (chips M10-A ativos).
  'openDoctorStationDisabledHint',
  // Consulta com chips de cobertura (M10-A) disponíveis: o toggle legado não é exibido.
  'hideOpenDoctorStation'
])

/* eslint-disable sonarjs/no-duplicate-string */
const emit = defineEmits([
  'update:company',
  'update:healthUnits',
  'update:schedules',
  'update:doctors',
  'update:positions',
  'update:shifts',
  'update:isOpenDoctorStation'
])

const healthUnitStore = useHealthUnitStore()
const scheduleStore = useScheduleStore()
const doctorStore = useDoctorStore()
const positionStore = usePositionStore()
const shiftStore = useShiftStore()

const { listCompanies } = storeToRefs(useCompanyStore())
const { listHealthUnitsByCompany } = storeToRefs(useHealthUnitStore())
const { listSchedules } = storeToRefs(useScheduleStore())
const { listDoctorsCompany } = storeToRefs(useDoctorStore())
const { listPositions } = storeToRefs(usePositionStore())
const { listShifts } = storeToRefs(useShiftStore())

const toIds = (values: SelectId[] | null | undefined) =>
  (values ?? []).map(toId).filter((id): id is number => id !== undefined)

const setCompany = (v: SelectId) => {
  const companyId = toId(v)
  emit('update:company', companyId)
  emit('update:healthUnits', undefined)
  emit('update:schedules', undefined)
  emit('update:shifts', undefined)
  emit('update:positions', undefined)
  healthUnitStore.setHealthUnitsByCompany(companyId ?? null)
  doctorStore.applyFilters({ companies: companyId ? [String(companyId)] : [] }, false)
}

const setHealthUnits = (healthUnits: SelectId[]) => {
  const healthUnitIds = toIds(healthUnits)
  emit('update:healthUnits', healthUnitIds)
  emit('update:schedules', undefined)
  emit('update:shifts', undefined)
  emit('update:positions', undefined)
  scheduleStore.setSchedulesByHealthUnits(healthUnitIds)
}

const setSchedules = async (schedules: SelectId[]) => {
  const scheduleIds = toIds(schedules)
  emit('update:schedules', scheduleIds)
  emit('update:positions', undefined)
  emit('update:shifts', undefined)

  await Promise.all([
    positionStore.setPositionsBySchedules(scheduleIds),
    shiftStore.setShiftsBySchedules(scheduleIds)
  ])
}

const setIsOpenDoctorStation = (value: any) => {
  const realValue = value === 'all' ? undefined : value
  emit('update:isOpenDoctorStation', realValue)

  if (realValue === true) {
    emit('update:doctors', undefined)
  }
}

const setShifts = (shifts: string[]) => {
  emit('update:shifts', shifts)
}

const setDoctors = (doctors: string[]) => {
  emit('update:doctors', doctors)
}

const setPositions = (positions: string[]) => {
  emit('update:positions', positions)
}
</script>
